import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Save and restore scroll position for the page's primary scroll container.
 *
 * Strategy:
 *   - Scroll positions are scoped per route (pathname + hash) so each tab
 *     remembers its own scroll independently.
 *   - sessionStorage so positions clear when the browser tab closes — avoids
 *     restoring stale offsets onto a vastly changed page after weeks away.
 *   - The container ref is intentionally generic so any page (or the global
 *     <main> in Layout) can plug in.
 *
 * Usage:
 *   const ref = useScrollRestore();
 *   return <main ref={ref}>...</main>;
 *
 * Or to scope to an inner container only:
 *   const ref = useScrollRestore({ key: "memories-list" });
 *   return <div ref={ref} className="scroll-area">...</div>;
 */
const STORAGE_PREFIX = "anna_scroll:";

export default function useScrollRestore(opts = {}) {
  const { key } = opts;
  const location = useLocation();
  const ref = useRef(null);
  // Only treat the very first scroll event as "user-driven" once; before that
  // the browser may emit a synthetic scroll-to-top on mount that we want to
  // ignore so it doesn't overwrite a saved position before we restore it.
  const restoredRef = useRef(false);

  // Per-route storage key. Including hash means tabs don't clobber each other.
  const storageKey = `${STORAGE_PREFIX}${key ?? location.pathname + location.hash}`;

  // Restore on mount / route change.
  useEffect(() => {
    restoredRef.current = false;
    const el = ref.current;
    if (!el) return;
    const saved = sessionStorage.getItem(storageKey);
    if (saved !== null) {
      const top = parseInt(saved, 10);
      if (!Number.isNaN(top)) {
        // Defer past the current paint so children that are still loading
        // don't overwrite our restore with their own first-paint scrollTo(0).
        requestAnimationFrame(() => {
          if (ref.current) ref.current.scrollTop = top;
          restoredRef.current = true;
        });
        return;
      }
    }
    restoredRef.current = true;
  }, [storageKey]);

  // Save on scroll (throttled with rAF + a trailing save on unmount).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    let lastTop = el.scrollTop;
    const save = () => {
      raf = 0;
      if (!restoredRef.current) return;
      sessionStorage.setItem(storageKey, String(lastTop));
    };
    const onScroll = () => {
      lastTop = el.scrollTop;
      if (!raf) raf = requestAnimationFrame(save);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      // Trailing write so a fast unmount still records the latest position.
      if (restoredRef.current) {
        sessionStorage.setItem(storageKey, String(lastTop));
      }
    };
  }, [storageKey]);

  return ref;
}
